// Country-flag emoji polyfill.
//
// Country flags are Unicode regional-indicator pairs (🇩🇪 = U+1F1E9 U+1F1EA). iOS, macOS and
// modern Android render them as flags; Windows and every Chromium-based desktop browser render
// the bare two-letter code ("DE") because their system emoji fonts omit flag glyphs. Our menu
// language pill and the language picker looked broken there.
//
// Fix: on exactly those platforms, load a small (~78 KB) webfont that DOES contain the flags and
// let CSS `font-family` fall through to it only for flag code points (scoped via `unicode-range`,
// so no other text is affected). Platforms with working native flags are detected and left
// untouched — they keep their nicer native flag art and never download the font.
//
// Detection + injection adapted from country-flag-emoji-polyfill (MIT, © 2022 TalkJS). The bundled
// font is a Twemoji subset (CC-BY 4.0) — see public/fonts/TwemojiCountryFlags.LICENSE.txt. We host
// the font locally (not the upstream CDN) so the app stays fully offline.

export const FLAG_FONT_FAMILY = 'Twemoji Country Flags';
const FLAG_FONT_URL = '/fonts/TwemojiCountryFlags.woff2';

// Renders `text` in a 1×1 canvas twice (white on nothing, black on nothing) and returns the pixel.
// If the glyph is a color emoji the two fills look identical and the pixel is non-transparent; a
// monochrome fallback (the "DE" letters) differs between the two fills.
function renderedColor(ctx: CanvasRenderingContext2D, text: string, color: string): string {
  ctx.clearRect(0, 0, 100, 100);
  ctx.fillStyle = color;
  ctx.fillText(text, 0, 0);
  return ctx.getImageData(0, 0, 1, 1).data.join(',');
}

function rendersAsColorEmoji(text: string): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.textBaseline = 'top';
  ctx.font = '100px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  ctx.scale(0.01, 0.01);
  const white = renderedColor(ctx, text, '#fff');
  const black = renderedColor(ctx, text, '#000');
  return white === black && !black.startsWith('0,0,0,');
}

/**
 * If the browser shows color emoji but NOT country flags (Windows / Chromium desktop), inject an
 * @font-face for the local flag font and preload it (so Phaser canvas text can use it too), then
 * return true. Otherwise do nothing and return false. Safe to call once at startup.
 */
export function initCountryFlagFont(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  // 😊 is a plain color emoji (supported everywhere that has emoji at all); 🇨🇭 is a country flag.
  // Emoji work but the flag doesn't → this is a platform that needs the polyfill.
  if (!rendersAsColorEmoji('\u{1F60A}') || rendersAsColorEmoji('\u{1F1E8}\u{1F1ED}')) return false;

  const style = document.createElement('style');
  style.textContent = `@font-face {
    font-family: "${FLAG_FONT_FAMILY}";
    unicode-range: U+1F1E6-1F1FF;
    src: url("${FLAG_FONT_URL}") format("woff2");
    font-display: swap;
  }`;
  document.head.appendChild(style);

  // Warm the font so it's ready when the Phaser celebration screen paints a flag to the canvas
  // (canvas text, unlike DOM, does not repaint itself when a webfont finishes loading later).
  if (document.fonts?.load) void document.fonts.load(`64px "${FLAG_FONT_FAMILY}"`, '\u{1F1E8}\u{1F1ED}');
  return true;
}
